import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { toLocalInputValue, fromLocalInputValue } from '../utils/dateInput';

const VenueFeaturedManager = ({ venueId, venue, onUpdated }) => {
    const { auth } = useAuth();
    const [isFeatured, setIsFeatured] = useState(false);
    const [featuredRank, setFeaturedRank] = useState('');
    const [featuredUntil, setFeaturedUntil] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!venue) return;
        setIsFeatured(Boolean(venue.is_featured));
        setFeaturedRank(venue.featured_rank ?? '');
        setFeaturedUntil(toLocalInputValue(venue.featured_until));
    }, [venue]);

    const isExpired = isFeatured && featuredUntil && new Date(featuredUntil) <= new Date();

    const handleSave = useCallback(async () => {
        if (!venueId) return;
        setSaving(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    is_featured: isFeatured,
                    featured_rank: isFeatured && featuredRank !== '' ? Number(featuredRank) : null,
                    featured_until: isFeatured && featuredUntil ? fromLocalInputValue(featuredUntil) : null
                })
            });

            let data = {};
            try {
                data = await response.json();
            } catch (e) {
                data = {};
            }

            if (response.ok) {
                toast.success('Featured placement updated');
                if (onUpdated) onUpdated();
            } else {
                toast.error(data.message || data.errors?.[0]?.message || 'Failed to update featured placement');
            }
        } catch (error) {
            console.error('Error updating featured placement:', error);
            toast.error('Failed to update featured placement');
        } finally {
            setSaving(false);
        }
    }, [auth, venueId, isFeatured, featuredRank, featuredUntil, onUpdated]);

    return (
        <div className="card shadow-none border radius-12">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2 py-16 px-24 border-bottom bg-white radius-top-12">
                <div>
                    <h6 className="mb-0 fw-bold text-dark">Featured Placement</h6>
                    <span className="text-secondary-light text-xs">
                        Featured venues are pinned to the top of Popular Places and of their category row in the app. Up to 3 venues can be featured at a time.
                    </span>
                </div>
                <button
                    type="button"
                    className="hs-btn-primary hs-btn-sm"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Placement'}
                </button>
            </div>
            <div className="card-body p-24">
                <div className="form-check form-switch mb-20">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="venue-is-featured"
                        checked={isFeatured}
                        onChange={(e) => setIsFeatured(e.target.checked)}
                    />
                    <label className="form-check-label fw-semibold text-sm" htmlFor="venue-is-featured">
                        Feature this venue
                    </label>
                </div>

                <div className="row gy-3">
                    <div className="col-md-6">
                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                            Featured rank
                        </label>
                        <input
                            type="number"
                            min="1"
                            className="form-control radius-8"
                            value={featuredRank}
                            onChange={(e) => setFeaturedRank(e.target.value)}
                            disabled={!isFeatured}
                            placeholder="1 = first"
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                            Featured until (optional)
                        </label>
                        <input
                            type="datetime-local"
                            className="form-control radius-8"
                            value={featuredUntil}
                            onChange={(e) => setFeaturedUntil(e.target.value)}
                            disabled={!isFeatured}
                        />
                        <small className="text-secondary-light text-xs">Leave empty to keep it featured until you switch it off.</small>
                    </div>
                </div>

                {isExpired && (
                    <div className="alert alert-warning mt-20 mb-0 text-sm">
                        This date has passed, so the venue is no longer pinned in the app.
                    </div>
                )}
            </div>
        </div>
    );
};

export default VenueFeaturedManager;
